import { createReducer, PayloadAction } from '@reduxjs/toolkit';
import { normalize } from 'normalizr';
import { MentorshipProgramGoalItemsSchema } from 'redux/schemas/api/mentoring-program-goal-items';
import {
  createMentorshipProgramGoalItem,
  deleteMentorshipProgramGoalItem,
  getMentorshipProgramGoalItems,
  MentoringProgramGoalResponse,
  setMentorshipProgramGoalsTotalCount,
  updateMentorshipProgramGoalItem,
} from 'redux/actions/mentoring-program-goal-items';
import { RootState } from 'redux/schemas';
import { initialRootState } from '.';

const setProgramGoalItemActionHandler = (state: RootState, action: ReturnType<typeof getMentorshipProgramGoalItems.fulfilled>) => {
  const { enrollmentId, connectionId, page } = action.meta.arg;
  const { entities: { mentorshipProgramGoalItems } } = normalize(action.payload, MentorshipProgramGoalItemsSchema);
  const stateKey = enrollmentId ?? connectionId;
  if (stateKey === null || stateKey === undefined) return;

  state.models.mentorshipProgramGoalItems[stateKey] = {
    ...state.models.mentorshipProgramGoalItems[stateKey],
    goals: {
      ...(page > 1 && state.models.mentorshipProgramGoalItems[stateKey]?.goals),
      ...mentorshipProgramGoalItems,
    },
  };
};

const addCreatedGoalItemHandler = (
  state: RootState,
  action: PayloadAction<MentoringProgramGoalResponse>,
) => {
  const { goalItem, enrollmentId, connectionId } = action.payload;
  const stateKey = enrollmentId ?? goalItem.assignedTo?.id ?? connectionId;
  if (stateKey === null || stateKey === undefined) return;
  if (!state.models.mentorshipProgramGoalItems[stateKey]) {
    state.models.mentorshipProgramGoalItems[stateKey] = { goals: {}, totalCount: 0 };
  }
  if (!state.models.mentorshipProgramGoalItems[stateKey].goals[goalItem.id]) {
    state.models.mentorshipProgramGoalItems[stateKey].totalCount = (state.models.mentorshipProgramGoalItems[stateKey].totalCount ?? 0) + 1;
  }
  state.models.mentorshipProgramGoalItems[stateKey].goals[goalItem.id] = goalItem;
};

const updateModifiedGoalItemHandler = (
  state: RootState,
  action: ReturnType<typeof updateMentorshipProgramGoalItem.fulfilled>,
) => {
  const { goalItem, enrollmentId } = action.payload;
  if (!state.models.mentorshipProgramGoalItems[enrollmentId]) return;
  state.models.mentorshipProgramGoalItems[enrollmentId].goals[goalItem.id] = goalItem;
};

const removeDeletedGoalItemHandler = (
  state: RootState,
  action: ReturnType<typeof deleteMentorshipProgramGoalItem.fulfilled>,
) => {
  const {
    meta: {
      arg: { enrollmentId, id: goalId },
    },
  } = action;
  delete state.models.mentorshipProgramGoalItems[enrollmentId].goals[goalId];
  state.models.mentorshipProgramGoalItems[enrollmentId].totalCount -= 1;
};

export default createReducer(initialRootState, (builder) => {
  builder
    .addCase(
      getMentorshipProgramGoalItems.fulfilled,
      setProgramGoalItemActionHandler,
    )
    .addCase(
      createMentorshipProgramGoalItem.fulfilled,
      addCreatedGoalItemHandler,
    )
    .addCase(
      updateMentorshipProgramGoalItem.fulfilled,
      updateModifiedGoalItemHandler,
    )
    .addCase(
      deleteMentorshipProgramGoalItem.fulfilled,
      removeDeletedGoalItemHandler,
    )
    .addCase(
      setMentorshipProgramGoalsTotalCount,
      (state: RootState, action: PayloadAction<{ enrollmentId: number, totalCount: number }>) => {
        const { enrollmentId, totalCount } = action.payload;
        state.models.mentorshipProgramGoalItems[enrollmentId] = {
          ...state.models.mentorshipProgramGoalItems[enrollmentId],
          totalCount,
        };
      },
    );
});
